import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

// Phase 8.5-05 — single hook that connects a list-screen route's URL
// state (read via TanStack Router) to the {filter, sort, page, pageSize}
// shape the rest of the app expects. URL is the single source of truth;
// React state is reserved for transient UI (open dialogs etc).
//
// Usage:
//
//   const { filter, sort, page, pageSize, updateQuery, resetQuery } =
//     useListQueryRoute({
//       from: '/_protected/accounts',
//       defaultFilter: { status: undefined as AccountStatus | undefined },
//       defaultSort: { field: 'createdAt', dir: 'desc' },
//     });
//
// The route's `validateSearch` zod schema handles wire-format coercion
// (string → number for `page`, etc.) — this hook only applies defaults
// for missing keys and emits typed `updateQuery` setter.
//
// `updateQuery({ filter, sort })` resets `page` to 1 (filter/sort
// changes invalidate the current offset). `updateQuery({ page })` keeps
// other fields untouched. `resetQuery()` clears every list-query field
// from the URL.

export type SortDir = 'asc' | 'desc';

export interface ListQuerySort<TSort extends string> {
  field: TSort | null;
  dir: SortDir;
}

export interface ListQueryRouteState<TFilter, TSort extends string> {
  filter: TFilter;
  sort: ListQuerySort<TSort>;
  page: number;
  pageSize: number;
}

export interface ListQueryRouteUpdate<TFilter, TSort extends string> {
  filter?: Partial<TFilter>;
  sort?: ListQuerySort<TSort>;
  page?: number;
  pageSize?: number;
}

export interface UseListQueryRouteOptions<TFilter, TSort extends string> {
  /** Route id, e.g. `'/_protected/accounts'`. */
  from: string;
  /** Filter keys + their default values (typically `undefined`). */
  defaultFilter: TFilter;
  /** Default sort applied when URL doesn't specify one. */
  defaultSort: { field: TSort; dir: SortDir };
  /** Page-size cap; defaults to 20 (matches ADR-014). */
  defaultPageSize?: number;
}

export interface ListQueryRouteResult<TFilter, TSort extends string> extends ListQueryRouteState<
  TFilter,
  TSort
> {
  /**
   * Merge `partial` into the URL state. Page resets to 1 on filter or
   * sort changes; explicit `page` in `partial` overrides that.
   */
  updateQuery: (partial: ListQueryRouteUpdate<TFilter, TSort>) => void;
  /** Drop every list-query key from the URL (back to defaults). */
  resetQuery: () => void;
}

interface RawSearch {
  filter?: Record<string, unknown>;
  sort?: string;
  page?: number | string;
  pageSize?: number | string;
}

const parseSortString = <TSort extends string>(
  raw: string | undefined,
  fallback: { field: TSort; dir: SortDir },
): ListQuerySort<TSort> => {
  if (!raw) return { field: fallback.field, dir: fallback.dir };
  const trimmed = raw.trim();
  if (trimmed === '') return { field: fallback.field, dir: fallback.dir };
  const dir: SortDir = trimmed.startsWith('-') ? 'desc' : 'asc';
  const field = (dir === 'desc' ? trimmed.slice(1) : trimmed) as TSort;
  return { field, dir };
};

const formatSortString = <TSort extends string>(sort: ListQuerySort<TSort>): string | undefined => {
  if (sort.field === null) return undefined;
  return sort.dir === 'desc' ? `-${sort.field}` : sort.field;
};

const coerceInt = (raw: unknown, fallback: number): number => {
  if (typeof raw === 'number' && Number.isInteger(raw)) return raw;
  if (typeof raw === 'string' && /^\d+$/.test(raw)) return Number.parseInt(raw, 10);
  return fallback;
};

/**
 * Strips `undefined` / `null` / empty-string entries so they're absent
 * from the URL rather than serialized as `?key=`.
 */
const compact = (obj: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'object' && !Array.isArray(v)) {
      const inner = compact(v as Record<string, unknown>);
      if (Object.keys(inner).length > 0) out[k] = inner;
      continue;
    }
    out[k] = v;
  }
  return out;
};

// TanStack Router's typed APIs require route ids to be known at compile
// time. This hook is intentionally generic over routes (it's reused on
// every list screen) so we drop into the loosely-typed shape via casts.
// Per-route validation lives in each route's `validateSearch` schema.
//
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseUseSearch = (opts: { from: any; strict: false }) => unknown;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseUseNavigate = () => (opts: any) => unknown;

export const useListQueryRoute = <TFilter, TSort extends string>(
  options: UseListQueryRouteOptions<TFilter, TSort>,
): ListQueryRouteResult<TFilter, TSort> => {
  const { from, defaultFilter, defaultSort, defaultPageSize = 20 } = options;
  const search = (useSearch as unknown as LooseUseSearch)({
    from,
    strict: false,
  }) as RawSearch;
  const navigate = (useNavigate as unknown as LooseUseNavigate)();

  const state = useMemo<ListQueryRouteState<TFilter, TSort>>(() => {
    const filter = { ...defaultFilter, ...(search.filter as Partial<TFilter> | undefined) };
    return {
      filter,
      sort: parseSortString(search.sort, defaultSort),
      page: coerceInt(search.page, 1),
      pageSize: coerceInt(search.pageSize, defaultPageSize),
    };
  }, [
    search.filter,
    search.sort,
    search.page,
    search.pageSize,
    defaultFilter,
    defaultSort,
    defaultPageSize,
  ]);

  const updateQuery = useCallback(
    (partial: ListQueryRouteUpdate<TFilter, TSort>): void => {
      void navigate({
        from,
        // TanStack Router 1.168 resolves `to` to `from` literally when `to`
        // is omitted — for layout-route ids like `/_protected/clients` that
        // would put the id into the URL bar (Not Found). `to: '.'` keeps
        // navigation on the current location, mutating only the search.
        to: '.',
        search: (prev: unknown) => {
          const prevSearch = (prev ?? {}) as RawSearch;
          const next: RawSearch = { ...prevSearch };

          if (partial.filter !== undefined) {
            const merged = {
              ...(prevSearch.filter ?? {}),
              ...(partial.filter as Record<string, unknown>),
            };
            const compactedFilter = compact(merged);
            if (Object.keys(compactedFilter).length > 0) {
              next.filter = compactedFilter;
            } else {
              delete next.filter;
            }
            next.page = 1;
          }
          if (partial.sort !== undefined) {
            const sortStr = formatSortString(partial.sort);
            if (sortStr === undefined) {
              delete next.sort;
            } else {
              next.sort = sortStr;
            }
            next.page = 1;
          }
          if (partial.page !== undefined) next.page = partial.page;
          if (partial.pageSize !== undefined) next.pageSize = partial.pageSize;
          return compact(next as unknown as Record<string, unknown>);
        },
        replace: true,
      });
    },
    [navigate, from],
  );

  const resetQuery = useCallback((): void => {
    void navigate({
      from,
      to: '.',
      search: () => ({}),
      replace: true,
    });
  }, [navigate, from]);

  return { ...state, updateQuery, resetQuery };
};

// Re-export for tests / callers wanting to assert on the URL serializer
// without importing through `lib/list-query`.
export { stringifyListQuerySearch, parseListQuerySearch } from './list-query';
